import {isAllOf} from '@reduxjs/toolkit';

import {showNewNotification} from '@edna/components';
import {TCurrency} from '@edna/utils';

import {TItemId} from 'src/constants';
import {TInvoice} from 'src/containers/Invoices/definitions';
import {EWebNotificationType, isMatchNotificationQuery} from 'src/containers/Notifications';
import {
  CACHE_LIFETIME,
  ECacheTag,
  isErrorWithCode,
  isMatchQueryMeta,
  listenerMiddleware,
  rootApi,
  showErrorNotification,
} from 'src/models';

import {
  EAutopaymentStatus,
  TAutopaymentFormData,
  TAutopaymentRestrictions,
  TGeneratePaymentLinkPayload,
  TGeneratePaymentLinkResponse,
  TPayKeeperAutopymentInfo,
  TPayKeeperInfo,
  TPayKeeperOrder,
  TStripeAutopayment,
  defaultStripeInvoiceParams,
} from './definitions';

enum EPayKeeperAction {
  AUTOPAYMENT_INFO = 'RECURRENT_CARD_BINDING',
  CONNECT_AUTOPAYMENT = 'CREATE_RECURRENT_CARD_BINDING',
  CARD_INFO = 'ONE_OFF_CARD_BINDING',
  SAVE_CARD = 'CREATE_ONE_OFF_CARD_BINDING',
  PAY = 'ONE_OFF_PAYMENT',
}

const api = rootApi.injectEndpoints({
  overrideExisting: 'throw',
  endpoints: (builder) => ({
    // Invoice
    generatePaymentLink: builder.mutation<
      TGeneratePaymentLinkResponse,
      TGeneratePaymentLinkPayload
    >({
      query: ({tenantId, ...data}) => ({
        method: 'POST',
        url: tenantId
          ? '/billing/document/support/bill/generate'
          : '/billing/document/bill/generate',
        params: {tenantId},
        data,
        meta: {isShowError: true},
      }),
    }),
    // Autopayment
    stripeRedirect: builder.query<{stripeUrl: string}, void>({
      query: () => ({
        method: 'GET',
        url: '/stripe/checkout/customer-portal',
      }),
    }),
    getStripeAutopaymentInfo: builder.query<TStripeAutopayment | undefined, TItemId | void>({
      query: (supportTenantId) => ({
        method: 'GET',
        url:
          supportTenantId !== undefined
            ? `/stripe/checkout/support/${supportTenantId}/customer-info`
            : '/stripe/checkout/customer-info',
      }),
      keepUnusedDataFor: CACHE_LIFETIME,
      providesTags: [ECacheTag.AUTOPAYMENT],
      transformResponse: (data: TStripeAutopayment) => {
        if (data?.customerStatus === EAutopaymentStatus.NEW) {
          return undefined;
        }

        return data;
      },
    }),
    getStripeAutopaymentRestrictions: builder.query<TAutopaymentRestrictions, void>({
      query: () => ({
        method: 'GET',
        url: '/stripe/checkout/restrictions',
      }),
      keepUnusedDataFor: CACHE_LIFETIME,
    }),
    connectStripeAutopayment: builder.mutation<TStripeAutopayment, TAutopaymentFormData>({
      query: (data) => ({
        method: 'POST',
        url: '/stripe/checkout/setup-customer',
        data,
        meta: {isShowError: true},
      }),
      invalidatesTags: [ECacheTag.AUTOPAYMENT],
    }),
    updateStripeAutopaymentSettings: builder.mutation<TStripeAutopayment, TAutopaymentFormData>({
      query: (data) => ({
        method: 'PUT',
        url: '/stripe/checkout/update-customer',
        data,
        meta: {isShowError: true},
      }),
      invalidatesTags: [ECacheTag.AUTOPAYMENT],
    }),
    offStripeAutopayment: builder.mutation<void, TItemId | void>({
      query: (supportTenantId) => ({
        method: supportTenantId !== undefined ? 'PUT' : 'GET',
        url:
          supportTenantId !== undefined
            ? `/stripe/checkout/support/${supportTenantId}/disable-customer`
            : '/stripe/checkout/request-deactivation',
        meta: {isShowError: true},
      }),
      invalidatesTags: (_, __, supportTenantId) => (supportTenantId ? [ECacheTag.AUTOPAYMENT] : []),
    }),
    chargeStripeAutopayment: builder.mutation<void, TItemId>({
      query: (supportTenantId) => ({
        method: 'POST',
        url: `/stripe/checkout/support/${supportTenantId}/initiate-auto-replenishment`,
        meta: {isShowError: true},
      }),
      invalidatesTags: (_, __, supportTenantId) => (supportTenantId ? [ECacheTag.AUTOPAYMENT] : []),
    }),
    restoreStripeAutopayment: builder.mutation<void, TItemId | void>({
      query: (supportTenantId) => ({
        method: 'POST',
        url: supportTenantId
          ? `/stripe/checkout/support/${supportTenantId}/enable-customer`
          : '/stripe/checkout/enable-customer',
        meta: {isShowError: true},
      }),
      invalidatesTags: [ECacheTag.AUTOPAYMENT],
    }),
    getPayKeeperAutopaymentInfo: builder.query<TPayKeeperAutopymentInfo | void, TItemId | void>({
      query: (tenantId) => ({
        method: 'GET',
        url: tenantId ? '/paykeeper/support/client/list' : '/paykeeper/client',
        params: {tenantId, type: EPayKeeperAction.AUTOPAYMENT_INFO},
      }),
      keepUnusedDataFor: CACHE_LIFETIME,
      providesTags: [ECacheTag.AUTOPAYMENT],
      transformResponse: (data: TPayKeeperAutopymentInfo[] | TPayKeeperAutopymentInfo) =>
        Array.isArray(data) ? data[0] : data,
    }),
    offPayKeeperAutopayment: builder.mutation<void, {tenantId?: TItemId; userId?: number} | void>({
      query: (data) => ({
        method: 'DELETE',
        url: data?.tenantId ? '/paykeeper/support/client' : '/paykeeper/client',
        meta: {isShowError: true},
        data: {...data, type: EPayKeeperAction.AUTOPAYMENT_INFO},
      }),
      invalidatesTags: [ECacheTag.AUTOPAYMENT],
    }),
    createPayKeeperAutopaymentOrder: builder.mutation<
      TPayKeeperOrder,
      {sum: number; recurrentPaymentSum: number}
    >({
      query: (data) => ({
        method: 'POST',
        url: '/paykeeper/orders',
        data: {...data, orderType: EPayKeeperAction.CONNECT_AUTOPAYMENT},
        meta: {isShowError: true},
      }),
      invalidatesTags: [ECacheTag.AUTOPAYMENT],
    }),
    updatePayKeeperAutopaymentSum: builder.mutation<void, number>({
      query: (sum) => ({
        method: 'PUT',
        url: '/paykeeper/client/recurrent',
        data: {sum},
        meta: {isShowError: true},
      }),
      invalidatesTags: [ECacheTag.AUTOPAYMENT],
    }),
    // OnetimePayment
    getPayKeeperInfo: builder.query<TPayKeeperInfo | void, void>({
      query: () => ({
        method: 'GET',
        url: '/paykeeper/client',
        params: {type: EPayKeeperAction.CARD_INFO},
      }),
      keepUnusedDataFor: CACHE_LIFETIME,
      providesTags: [ECacheTag.ONETIME_PAYMENT],
    }),
    getTenantPayKeeperInfo: builder.query<TPayKeeperInfo[] | void, TItemId>({
      query: (tenantId) => ({
        method: 'GET',
        url: '/paykeeper/support/client/list',
        params: {tenantId, type: EPayKeeperAction.CARD_INFO},
      }),
      keepUnusedDataFor: CACHE_LIFETIME,
      providesTags: [ECacheTag.ONETIME_PAYMENT],
    }),
    createPayKeeperOrder: builder.mutation<TPayKeeperOrder, {sum: number; isSaveCard?: boolean}>({
      query: ({sum, isSaveCard}) => ({
        method: 'POST',
        url: '/paykeeper/orders',
        data: {sum, orderType: isSaveCard ? EPayKeeperAction.SAVE_CARD : EPayKeeperAction.PAY},
        meta: {isShowError: true},
      }),
      invalidatesTags: [ECacheTag.ONETIME_PAYMENT],
    }),
    payWithPayKeeper: builder.mutation<void, number>({
      query: (sum) => ({
        method: 'POST',
        url: '/paykeeper/initiate-payment',
        data: {sum},
        meta: {isShowError: true},
      }),
      invalidatesTags: [ECacheTag.ONETIME_PAYMENT],
    }),
    deletePayKeeperCard: builder.mutation<void, {tenantId: TItemId; userId: number} | void>({
      query: (data) => ({
        method: 'DELETE',
        url: data ? '/paykeeper/support/client' : '/paykeeper/client',
        meta: {isShowError: true},
        data: {...data, type: EPayKeeperAction.CARD_INFO},
      }),
      invalidatesTags: [ECacheTag.ONETIME_PAYMENT],
    }),

    createStripeInvoice: builder.mutation<TInvoice, {amount: number; currency?: TCurrency}>({
      query: (data) => ({
        method: 'POST',
        url: `/invoice/${defaultStripeInvoiceParams.kind}/create`,
        data: {...data, ...defaultStripeInvoiceParams},
      }),
    }),
  }),
});

listenerMiddleware.startListening({
  matcher: api.endpoints.createStripeInvoice.matchFulfilled,
  effect: (data) => {
    if (!data.payload?.paymentUrl) {
      showNewNotification({
        type: 'ERROR',
        messageKey: 'UserCompanyProfile:serviceUnavailable',
      });
    }
  },
});

listenerMiddleware.startListening({
  matcher: isAllOf(
    api.endpoints.createStripeInvoice.matchRejected,
    isMatchQueryMeta({isBackendError: true}),
  ),
  effect: (data: TAnyObject) => {
    if (isErrorWithCode(data.payload)) {
      showErrorNotification(data.payload);
    } else {
      showNewNotification({
        type: 'ERROR',
        messageKey: 'UserCompanyProfile:serviceUnavailable',
      });
    }
  },
});

listenerMiddleware.startListening({
  matcher: isMatchNotificationQuery(EWebNotificationType.StripeCheckout),
  effect: (_, listenerApi) => {
    listenerApi.dispatch(api.util.invalidateTags([ECacheTag.AUTOPAYMENT]));
  },
});

listenerMiddleware.startListening({
  matcher: isMatchNotificationQuery(EWebNotificationType.PaykeeperPayment),
  effect: (_, listenerApi) => {
    listenerApi.dispatch(api.util.invalidateTags([ECacheTag.ONETIME_PAYMENT]));
  },
});

export const {
  // Invoice
  useGeneratePaymentLinkMutation,

  // Autopayment
  useLazyStripeRedirectQuery,
  useGetStripeAutopaymentInfoQuery,
  useGetStripeAutopaymentRestrictionsQuery,
  useOffStripeAutopaymentMutation,
  useConnectStripeAutopaymentMutation,
  useChargeStripeAutopaymentMutation,
  useUpdateStripeAutopaymentSettingsMutation,
  useRestoreStripeAutopaymentMutation,

  useGetPayKeeperAutopaymentInfoQuery,
  useOffPayKeeperAutopaymentMutation,
  useCreatePayKeeperAutopaymentOrderMutation,
  useUpdatePayKeeperAutopaymentSumMutation,

  // OnetimePayment
  useGetPayKeeperInfoQuery,
  useGetTenantPayKeeperInfoQuery,
  useCreatePayKeeperOrderMutation,
  useDeletePayKeeperCardMutation,
  usePayWithPayKeeperMutation,

  useCreateStripeInvoiceMutation,
} = api;
